import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

interface User {
  id: number;
  username: string;
  role: string;
  requests_count: number;
  referral_earnings?: number;
  telegram_chat_id?: string;
  created_at: string;
  token_count: number;
}

const userSchema = z.object({
  role: z.string().min(1, 'Роль обязательна'),
  requests_count: z.number().min(0, 'Количество запросов не может быть отрицательным'),
  telegram_chat_id: z.string().optional(),
});

const passwordSchema = z.object({
  new_password: z.string().min(6, 'Пароль должен содержать минимум 6 символов'),
});

const availableRoles = [
  { value: 'Обычный пользователь', label: 'Обычный пользователь' },
  { value: 'Инфинити', label: 'Инфинити' },
  { value: 'Администратор', label: 'Администратор' },
];

export function UsersManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [rolePopoverOpen, setRolePopoverOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const form = useForm<z.infer<typeof userSchema>>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      role: '',
      requests_count: 0,
      telegram_chat_id: '',
    },
  });

  const passwordForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      new_password: '',
    },
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  // Автоматически скрываем сообщения через 3 секунды
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/admin/users', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.users) {
          setUsers(data.users);
        } else if (data.ok && data.data) {
          setUsers(data.data);
        } else {
          console.error('Unexpected response format:', data);
        }
      } else {
        console.error('HTTP Error:', response.status);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    form.reset({
      role: user.role,
      requests_count: user.requests_count,
      telegram_chat_id: user.telegram_chat_id || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateUser = async (values: z.infer<typeof userSchema>) => {
    if (!selectedUser) return;

    setIsUpdating(true);
    try {
      const formData = new FormData();
      formData.append('role', values.role);
      formData.append('requests_count', values.requests_count.toString());
      if (values.telegram_chat_id) {
        formData.append('telegram_chat_id', values.telegram_chat_id);
      }

      const response = await fetch(`/api/auth/admin/users/${selectedUser.id}/update`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (response.ok && (data.ok || data.message)) {
        setIsEditDialogOpen(false);
        setMessage({ type: 'success', text: data.message || 'Пользователь успешно обновлен' });
        fetchUsers();
      } else {
        setMessage({ type: 'error', text: data.error || 'Ошибка при обновлении пользователя' });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setMessage({ type: 'error', text: 'Ошибка при обновлении пользователя' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!confirm('Вы уверены, что хотите удалить этого пользователя?')) return;

    try {
      const response = await fetch(`/api/auth/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok && (data.ok || data.message)) {
        setMessage({ type: 'success', text: data.message || 'Пользователь успешно удален' });
        fetchUsers();
      } else {
        setMessage({ type: 'error', text: data.error || 'Ошибка при удалении пользователя' });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setMessage({ type: 'error', text: 'Ошибка при удалении пользователя' });
    }
  };

  const handleChangePassword = (user: User) => {
    setSelectedUser(user);
    passwordForm.reset({
      new_password: '',
    });
    setIsPasswordDialogOpen(true);
  };

  const handleUpdatePassword = async (values: z.infer<typeof passwordSchema>) => {
    if (!selectedUser) return;

    setIsChangingPassword(true);
    try {
      const formData = new FormData();
      formData.append('new_password', values.new_password);

      const response = await fetch(`/api/auth/admin/users/${selectedUser.id}/change_password`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (response.ok && (data.ok || data.message)) {
        setIsPasswordDialogOpen(false);
        setMessage({ type: 'success', text: data.message || 'Пароль успешно изменен' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Ошибка при изменении пароля' });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setMessage({ type: 'error', text: 'Ошибка при изменении пароля' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Администратор':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
      case 'Инфинити':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
      case 'Обычный пользователь':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Управление пользователями</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Загрузка пользователей...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Управление пользователями</h2>
            <p className="text-muted-foreground">Просмотр и редактирование пользователей системы</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/admin">← Назад к обзору</Link>
          </Button>
        </div>
        
        {/* Уведомления */}
        {message && (
          <div className={`p-4 rounded-lg border ${
            message.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900 dark:border-green-700 dark:text-green-200'
              : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900 dark:border-red-700 dark:text-red-200'
          }`}>
            {message.text}
          </div>
        )}
        
        <Card>
          <CardHeader>
            <CardTitle>Пользователи системы</CardTitle>
          </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {users.map((user) => (
            <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{user.username}</span>
                  <span className={`text-xs px-2 py-1 rounded ${getRoleColor(user.role)}`}>
                    {user.role}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  ID: {user.id} • Запросов: {user.requests_count} • Токенов: {user.token_count}
                </div>
                {user.telegram_chat_id && (
                  <div className="text-sm text-muted-foreground">
                    Telegram: {user.telegram_chat_id}
                  </div>
                )}
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditUser(user)}
                >
                  Редактировать
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePassword(user)}
                >
                  Сменить пароль
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteUser(user.id)}
                >
                  Удалить
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Редактировать пользователя</DialogTitle>
              <DialogDescription>
                Изменение данных пользователя {selectedUser?.username}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleUpdateUser)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Роль</FormLabel>
                      <Popover open={rolePopoverOpen} onOpenChange={setRolePopoverOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between"
                            >
                              {field.value
                                ? availableRoles.find(
                                    (role) => role.value === field.value
                                  )?.label
                                : "Выберите роль..."}
                              <svg
                                className="ml-2 h-4 w-4 shrink-0 opacity-50"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                              >
                                <path d="m6 9 6 6 6-6" />
                              </svg>
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Поиск роли..." />
                            <CommandEmpty>Роль не найдена.</CommandEmpty>
                            <CommandGroup>
                              {availableRoles.map((role) => (
                                <CommandItem
                                  key={role.value}
                                  value={role.value}
                                  onSelect={(currentValue) => {
                                    field.onChange(currentValue);
                                    setRolePopoverOpen(false);
                                  }}
                                >
                                  <svg
                                    className={`mr-2 h-4 w-4 ${
                                      field.value === role.value
                                        ? "opacity-100"
                                        : "opacity-0"
                                    }`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  {role.label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="requests_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Количество запросов</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="telegram_chat_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telegram Chat ID</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditDialogOpen(false)}
                    disabled={isUpdating}
                  >
                    Отмена
                  </Button>
                  <Button type="submit" disabled={isUpdating}>
                    {isUpdating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Сохранение...
                      </>
                    ) : (
                      'Сохранить'
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Сменить пароль</DialogTitle>
              <DialogDescription>
                Изменение пароля для пользователя {selectedUser?.username}
              </DialogDescription>
            </DialogHeader>
            <Form {...passwordForm}>
              <form onSubmit={passwordForm.handleSubmit(handleUpdatePassword)} className="space-y-4">
                <FormField
                  control={passwordForm.control}
                  name="new_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Новый пароль</FormLabel>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="Введите новый пароль"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsPasswordDialogOpen(false)}
                    disabled={isChangingPassword}
                  >
                    Отмена
                  </Button>
                  <Button type="submit" disabled={isChangingPassword}>
                    {isChangingPassword ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Изменение...
                      </>
                    ) : (
                      'Сменить пароль'
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
